import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'
import { Card, Table, Button, Tag } from 'antd'
import {
  getAllTierFeatureList,
  getTierFeature,
  createTierFeature,
  updateTierFeature
} from '../../modules/tierFeature'
import { useDeepEffect } from '../../utils/hooks'
import TierFeatureForm from './components/TierFeatureForm'

const boolTag = (value) =>
  value ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>

const limitText = (value) => (value === 0 ? 'ไม่จำกัด' : value)

// คนละ convention กับ limitText ด้านบนโดยตั้งใจ (spec 082 D-17)
const tagLimitText = (value) => {
  if (value === -1) return 'ไม่จำกัด'
  if (value === 0 || value == null) return 'สร้างเองไม่ได้'
  return value
}

const TierFeature = () => {
  const dispatch = useDispatch()
  const [visible, setVisible] = useState(false)
  const [formData, setFormData] = useState({})

  const { tierFeatureList, isLoading } = useSelector(
    (state) => ({
      tierFeatureList: state.tierFeature.tierFeatureList,
      isLoading: state.tierFeature.isLoading
    }),
    []
  )

  useDeepEffect(() => {
    async function fetch() {
      await dispatch(getAllTierFeatureList())
    }

    fetch()
  }, [])

  const refetchData = async () => {
    await dispatch(getAllTierFeatureList())
  }

  const onCreate = () => {
    setFormData({})
    setVisible(true)
  }

  const onEdit = async (record) => {
    const fresh = await dispatch(getTierFeature(record.tier))
    setFormData(fresh || record)
    setVisible(true)
  }

  const onCloseModal = () => {
    setVisible(false)
    setFormData({})
  }

  const onOk = async (data) => {
    let success
    if (!_.isEmpty(formData)) {
      success = await dispatch(updateTierFeature(data))
    } else {
      success = await dispatch(createTierFeature(data))
    }

    if (success) {
      await dispatch(getAllTierFeatureList())
      setVisible(false)
      setFormData({})
    }
  }

  const columns = [
    {
      title: 'Tier',
      dataIndex: 'tier',
      key: 'tier',
      width: 100,
      align: 'center'
    },
    {
      title: 'Alert Setting',
      dataIndex: 'alertSetting',
      key: 'alertSetting',
      width: 110,
      align: 'center',
      render: boolTag
    },
    {
      title: 'Limit Weighing',
      dataIndex: 'limitWeighing',
      key: 'limitWeighing',
      width: 130,
      align: 'center',
      render: limitText
    },
    {
      title: 'Limit Accounting',
      dataIndex: 'limitAccounting',
      key: 'limitAccounting',
      width: 140,
      align: 'center',
      render: limitText
    },
    {
      title: 'Customer Tag',
      dataIndex: 'limitCustomerTag',
      key: 'limitCustomerTag',
      width: 140,
      align: 'center',
      render: tagLimitText
    },
    {
      title: 'Email Report',
      dataIndex: 'sentEmailReport',
      key: 'sentEmailReport',
      width: 110,
      align: 'center',
      render: boolTag
    },
    {
      title: 'AI Access',
      dataIndex: 'aiAccess',
      key: 'aiAccess',
      width: 100,
      align: 'center',
      render: boolTag
    },
    {
      title: 'Watermark Removed',
      dataIndex: 'watermarkRemoved',
      key: 'watermarkRemoved',
      width: 150,
      align: 'center',
      render: boolTag
    },
    {
      title: 'Price Scraper',
      dataIndex: 'priceScraper',
      key: 'priceScraper',
      width: 110,
      align: 'center',
      render: boolTag
    },
    {
      title: 'Live Dashboard',
      dataIndex: 'liveDashboard',
      key: 'liveDashboard',
      width: 130,
      align: 'center',
      render: boolTag
    },
    {
      title: 'Tool Access',
      dataIndex: 'toolAccess',
      key: 'toolAccess',
      width: 110,
      align: 'center',
      render: boolTag
    },
    {
      title: 'Auction Access',
      dataIndex: 'auctionAccess',
      key: 'auctionAccess',
      width: 130,
      align: 'center',
      render: boolTag
    },
    {
      title: 'Recycle News',
      dataIndex: 'recycleNews',
      key: 'recycleNews',
      width: 130,
      align: 'center',
      render: boolTag
    },
    {
      title: 'Shop Layout 3D',
      dataIndex: 'shopLayout',
      key: 'shopLayout',
      width: 140,
      align: 'center',
      render: boolTag
    },
    {
      title: 'Tracking',
      dataIndex: 'tracking',
      key: 'tracking',
      width: 100,
      align: 'center',
      render: boolTag
    },
    {
      title: 'QC Max Image (6 pics)',
      dataIndex: 'qcImage',
      key: 'qcImage',
      width: 150,
      align: 'center',
      render: (value) => boolTag(value === 6)
    },
    {
      title: 'Cash Drawer',
      dataIndex: 'cashDrawer',
      key: 'cashDrawer',
      width: 120,
      align: 'center',
      render: boolTag
    },
    {
      title: 'Custom Material',
      dataIndex: 'customMaterial',
      key: 'customMaterial',
      width: 140,
      align: 'center',
      render: boolTag
    },
    {
      title: 'Limit Custom Material',
      dataIndex: 'limitCustomMaterial',
      key: 'limitCustomMaterial',
      width: 170,
      align: 'center',
      render: limitText
    },
    {
      // 0 = ใช้ไม่ได้ ต่างจาก limitText (green2get-app spec 143)
      title: 'Secondary Camera',
      dataIndex: 'secondaryCamera',
      key: 'secondaryCamera',
      width: 150,
      align: 'center',
      render: (value) => value || 0
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 80,
      align: 'center',
      render: (text, record) => (
        <Button
          type="primary"
          size="small"
          shape="circle"
          icon="edit"
          onClick={() => onEdit(record)}
        />
      )
    }
  ]

  return (
    <Card
      title="Tier"
      extra={
        <div>
          <Button size="small" icon="plus" onClick={onCreate}>
            Add Tier
          </Button>
          <Button size="small" icon="reload" onClick={refetchData}>
            Reload
          </Button>
        </div>
      }>
      <Table
        loading={isLoading}
        scroll={{ x: 1700 }}
        rowKey={(record) => record.tier}
        dataSource={tierFeatureList}
        columns={columns}
      />
      <TierFeatureForm
        visible={visible}
        onOk={onOk}
        onCancel={onCloseModal}
        formData={formData}
        existingTiers={(tierFeatureList || []).map((row) => row.tier)}
      />
    </Card>
  )
}

export default TierFeature
